import { useState, type ReactNode } from "react";
import { useNav } from "../context/NavContext";
import { useAuth } from "../context/AuthContext";
import { Button, Card, Logo, Pill, SectionLabel } from "../components/ui";
import { Ic } from "../components/icons";
import { Cadastre } from "../components/Cadastre";
import { SignInDialog } from "../components/SignInDialog";
import { PublicHeader } from "../components/PublicHeader";
import { openCookieSettings } from "../lib/consent";

export default function Landing() {
  const { go } = useNav();
  const { user } = useAuth();
  const [addr, setAddr] = useState("");
  const [signInOpen, setSignInOpen] = useState(false);

  return (
    <div className="min-h-screen bg-canvas">
      <PublicHeader active="landing" />

      <SignInDialog
        open={signInOpen}
        onClose={() => setSignInOpen(false)}
        onSuccess={() => go("dashboard")}
      />

      <section className="relative">
        <div className="max-w-[1240px] mx-auto px-4 sm:px-6 pt-8 sm:pt-10 pb-12 sm:pb-20">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            <div className="lg:col-span-7">
              <Pill
                tone="lime"
                icon={<Ic.Bolt size={12} />}
                className="mb-6"
              >
                Couverture nationale · France entière
              </Pill>
              <h1 className="text-[32px] sm:text-[44px] lg:text-[56px] leading-[1.1] lg:leading-[1.02] font-semibold tracking-[-0.02em] text-ink-900">
                Révélez le potentiel{" "}
                <span className="font-serif italic font-normal">caché</span> de
                chaque bien.
              </h1>
              <p className="mt-5 text-[17px] text-ink-600 max-w-[560px] leading-relaxed">
                Arpentia, c'est{" "}
                <strong className="text-ink-900">l'arpenteur du XXI<sup>e</sup> siècle</strong>{" "}
                pour les agents immo. À partir d'une simple adresse, notre IA
                lit le PLU, croise le cadastre et analyse les DVF pour générer
                en 60 secondes une fiche « potentiel » prête à remettre à votre
                client.
              </p>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  user ? go("nouvelle") : setSignInOpen(true);
                }}
                className="mt-6 sm:mt-8 bg-paper hairline rounded-2xl sm:rounded-full p-1.5 pl-4 sm:pl-5 flex flex-wrap sm:flex-nowrap items-center gap-3 shadow-card max-w-[620px]"
              >
                <Ic.Pin size={16} className="text-ink-500 shrink-0" />
                <input
                  value={addr}
                  onChange={(e) => setAddr(e.target.value)}
                  className="flex-1 bg-transparent outline-none text-[14.5px] py-3 placeholder:text-ink-400"
                  placeholder="12 rue des Lilas, 92100 Boulogne-Billancourt"
                />
                <Button
                  variant="primary"
                  size="md"
                  iconRight={<Ic.Arrow size={14} />}
                >
                  Voir le potentiel
                </Button>
              </form>

              <div className="mt-4 flex items-center gap-3 sm:gap-5 flex-wrap text-[12.5px] text-ink-500">
                <span className="flex items-center gap-1.5">
                  <Ic.Check size={14} className="text-forest" /> Aucune carte
                  bleue
                </span>
                <span className="flex items-center gap-1.5">
                  <Ic.Check size={14} className="text-forest" /> 1 fiche
                  offerte · 7 j
                </span>
                <span className="flex items-center gap-1.5">
                  <Ic.Check size={14} className="text-forest" /> Export PDF
                  illimité
                </span>
              </div>
            </div>

            <div className="lg:col-span-5">
              <HeroVisual />
            </div>
          </div>
        </div>
      </section>

      <TrustStrip />

      <section id="how" className="max-w-[1240px] mx-auto px-4 sm:px-6 py-12 md:py-24">
        <div className="flex items-end justify-between gap-6 mb-8 sm:mb-10 flex-wrap">
          <div>
            <SectionLabel>Comment ça marche</SectionLabel>
            <h2 className="mt-3 text-[24px] sm:text-[32px] lg:text-[38px] leading-tight tracking-tight font-semibold max-w-[560px]">
              Trois étapes,{" "}
              <span className="font-serif italic font-normal">
                soixante secondes
              </span>
              .
            </h2>
          </div>
          <p className="text-ink-600 max-w-[360px] text-[14.5px]">
            Plus besoin de fouiller des PDF municipaux ou d’appeler la mairie.
            Arpentia orchestre tout pour vous.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
          <StepCard
            n="01"
            title="Une adresse suffit"
            desc="Tapez l’adresse du bien, ou glissez un PDF d’annonce — l’IA remplit les champs. Autocomplétion via la Base Adresse Nationale."
            icon={<Ic.Pin size={18} />}
          />
          <StepCard
            n="02"
            title="Analyse croisée"
            desc="PLU, cadastre, DVF, servitudes, risques, Géoportail de l’urbanisme — le tout, en arrière-plan."
            icon={<Ic.Cpu size={18} />}
            tone="lime"
          />
          <StepCard
            n="03"
            title="Fiche client-ready"
            desc="Une fiche PDF à votre marque, prête à remettre ou à intégrer dans votre argumentaire de mandat."
            icon={<Ic.File size={18} />}
          />
        </div>
      </section>

      <section id="product" className="max-w-[1240px] mx-auto px-4 sm:px-6 pb-12 md:pb-24">
        <div className="mb-8 sm:mb-10">
          <SectionLabel>Pour deux moments-clés</SectionLabel>
          <h2 className="mt-3 text-[24px] sm:text-[32px] lg:text-[38px] leading-tight tracking-tight font-semibold max-w-[720px]">
            Un même outil. Deux conversations{" "}
            <span className="font-serif italic font-normal">décisives</span>.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4 sm:gap-5">
          <MomentCard
            tag="Côté vendeur"
            title="Prise de mandat"
            quote="Votre maison vaut +18 % si on valorise l’extension de 32 m² autorisée par le PLU."
            body="Arrivez chez le propriétaire avec un argument chiffré, pas un baratin. La fiche fait le travail : chiffre clé, justification réglementaire, comparables DVF."
            features={[
              "Chiffre clé valorisant",
              "Comparables de vente",
              "Branding à votre agence",
            ]}
            visual="mandat"
          />
          <MomentCard
            tag="Côté acheteur"
            title="Post-visite"
            quote="Cette maison a un potentiel d’extension de +47 m². Prenez-la avant qu’un autre n’y pense."
            body="À la fin d’une visite, remettez une fiche qui déclenche la décision. Les acheteurs projettent — les offres arrivent plus vite."
            features={[
              "PDF envoyé en 1 clic",
              "Scénarios chiffrés",
              "Démarches (DP ou PC)",
            ]}
            visual="acheteur"
            tone="lime"
          />
        </div>
      </section>

      <section id="features" className="max-w-[1240px] mx-auto px-4 sm:px-6 pb-12 md:pb-24">
        <div className="mb-8 sm:mb-10">
          <SectionLabel>Ce que contient la fiche</SectionLabel>
          <h2 className="mt-3 text-[24px] sm:text-[32px] lg:text-[38px] leading-tight tracking-tight font-semibold max-w-[720px]">
            Six analyses. Une seule{" "}
            <span className="font-serif italic font-normal">
              source de vérité
            </span>
            .
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
          <FeatureCard
            icon={<Ic.Layers size={18} />}
            title="Zonage PLU"
            desc="Zone réglementaire, règles applicables, extrait officiel, version datée du règlement communal."
          />
          <FeatureCard
            icon={<Ic.Ruler size={18} />}
            title="Extension possible"
            desc="Surface d’extension autorisée au sol, emprise résiduelle, recul minimum aux limites séparatives."
          />
          <FeatureCard
            icon={<Ic.Building size={18} />}
            title="Surélévation"
            desc="Niveaux autorisés, hauteur absolue, hauteur à l’égout, faisabilité structurelle indicative."
          />
          <FeatureCard
            icon={<Ic.Grid size={18} />}
            title="Division parcellaire"
            desc="Faisabilité, nombre de lots potentiels, desserte voirie, servitude de passage."
          />
          <FeatureCard
            icon={<Ic.Shield size={18} />}
            title="Servitudes & risques"
            desc="PPRI, PPRN, monuments historiques, ABF, AVAP — tout ce qui peut stopper un projet."
          />
          <FeatureCard
            icon={<Ic.TrendUp size={18} />}
            title="Comparables DVF"
            desc="Transactions récentes dans un rayon de 500 m, prix au m² corrigé, tendance sur 24 mois."
          />
        </div>
      </section>

      <section className="bg-ink-900 text-white">
        <div className="max-w-[1240px] mx-auto px-4 sm:px-6 py-12 md:py-24">
          <div className="mb-8 sm:mb-12 flex items-end justify-between gap-6 flex-wrap">
            <div>
              <SectionLabel className="!text-lime-300">
                Ils ont signé des mandats
              </SectionLabel>
              <h2 className="mt-3 text-[24px] sm:text-[32px] lg:text-[38px] leading-tight tracking-tight font-semibold max-w-[720px]">
                La différence se fait au{" "}
                <span className="font-serif italic font-normal">
                  rendez-vous
                </span>
                .
              </h2>
            </div>
            <div className="text-[13px] text-ink-400 max-w-[320px]">
              Témoignages réels d’agents qui utilisent Arpentia depuis plus de 6
              mois.
            </div>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Testimonial
              name="Camille P."
              role="Agent indépendante · Boulogne"
              body="En RDV mandat, je sors la fiche et je montre +47 m² d’extension. Le propriétaire ne m’écoute plus — il m’écoute vraiment. J’ai signé 3 mandats en un mois."
            />
            <Testimonial
              name="Mehdi L."
              role="Directeur d’agence · Versailles"
              tone="lime"
              body="Ce qui me tuait, c’étaient les 2h à éplucher un PLU avant chaque visite. Aujourd’hui : 60 secondes, PDF à ma marque, je passe à autre chose."
            />
            <Testimonial
              name="Julie B."
              role="Négociatrice · Lyon 6"
              body="Un acheteur m’a dit « c’est la première fois qu’on m’aide autant à me projeter ». Offre au prix deux jours plus tard."
            />
          </div>
        </div>
      </section>

      {/* Brand-story bridge — connects the etymology of "Arpentia" to the
          value prop without forcing the visitor onto the brand page. */}
      <section className="max-w-[1240px] mx-auto px-4 sm:px-6 py-12 md:py-24">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-5">
            <SectionLabel>Pourquoi Arpentia ?</SectionLabel>
            <h2 className="mt-3 text-[24px] sm:text-[32px] lg:text-[38px] leading-tight tracking-tight font-semibold">
              10 siècles d'arpentage,{" "}
              <span className="font-serif italic font-normal">
                30 secondes de réponse
              </span>
              .
            </h2>
            <p className="mt-5 text-[15px] text-ink-700 leading-relaxed">
              En 1554, Henri II nommait des arpenteurs royaux pour contrôler le
              cadastre de Bretagne. Ils mesuraient la terre, lisaient les
              contraintes, révélaient le potentiel.
            </p>
            <p className="mt-3 text-[15px] text-ink-700 leading-relaxed">
              <strong className="text-ink-900">Arpentia</strong> reprend ce
              flambeau&nbsp;: l'<em>Arpent</em> ancestral de la mesure foncière
              + l'<em>IA</em> qui automatise la lecture du PLU. La même
              fonction. Un outil moderne.
            </p>
            <Button
              variant="outline"
              size="md"
              className="mt-6"
              iconRight={<Ic.Arrow size={14} />}
              onClick={() => go("brand")}
            >
              Lire notre histoire
            </Button>
          </div>

          <div className="lg:col-span-7">
            <Card className="p-6 sm:p-8">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-500">
                    Hier
                  </div>
                  <div className="mt-3 text-[15px] text-ink-800 leading-relaxed">
                    L'arpenteur royal, sa perche et sa corde. 3 heures par
                    parcelle pour mesurer, croiser le cadastre et estimer le
                    potentiel.
                  </div>
                  <div className="mt-4 inline-flex items-center gap-2 text-[12px] text-ink-500">
                    <Ic.Clock size={12} /> ~3 heures
                  </div>
                </div>
                <div className="border-t sm:border-t-0 sm:border-l border-ink-200/70 pt-5 sm:pt-0 sm:pl-6">
                  <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-lime-700">
                    Aujourd'hui
                  </div>
                  <div className="mt-3 text-[15px] text-ink-800 leading-relaxed">
                    Arpentia&nbsp;: une adresse, l'IA lit le PLU, croise le
                    cadastre et les DVF. Une fiche complète, prête à remettre
                    au client.
                  </div>
                  <div className="mt-4 inline-flex items-center gap-2 text-[12px] text-forest font-medium">
                    <Ic.Bolt size={12} /> ~30 secondes
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-ink-200/70 flex items-baseline gap-2 flex-wrap">
                <span className="text-[44px] sm:text-[56px] font-semibold tracking-tight text-ink-900">
                  Arpent
                </span>
                <span className="text-[44px] sm:text-[56px] font-serif italic text-lime-700">
                  ia
                </span>
                <span className="text-[13px] text-ink-500 ml-2">
                  = Arpenteur ancestral + IA moderne
                </span>
              </div>
            </Card>
          </div>
        </div>
      </section>

      <section id="pricing" className="max-w-[1240px] mx-auto px-4 sm:px-6 py-12 md:py-24">
        <div className="mb-8 sm:mb-10 flex items-end justify-between gap-6 flex-wrap">
          <div>
            <SectionLabel>Tarifs</SectionLabel>
            <h2 className="mt-3 text-[24px] sm:text-[32px] lg:text-[38px] leading-tight tracking-tight font-semibold max-w-[720px]">
              À partir de{" "}
              <span className="font-serif italic font-normal">
                4,23&nbsp;€/fiche
              </span>{" "}
              — jusqu’à 3× moins cher que nos concurrents.
            </h2>
          </div>
          <Button
            variant="outline"
            size="md"
            iconRight={<Ic.Arrow size={14} />}
            onClick={() => go("pricing")}
          >
            Voir tous les plans
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
          <PricingTeaser
            plan="Starter"
            price="44"
            desc="Pour tester Arpentia avant d'embarquer l'équipe."
            features={[
              "5 fiches / mois",
              "Export PDF à votre marque",
              "Support email",
            ]}
            onClick={() => go("pricing")}
          />
          <PricingTeaser
            plan="Négociateur"
            price="89"
            desc="Le sweet-spot pour 1 à 2 mandats par mois."
            features={[
              "15 fiches / mois",
              "Crédits add-on à 14 €",
              "Modèles PDF personnalisés",
            ]}
            highlight
            onClick={() => go("pricing")}
          />
          <PricingTeaser
            plan="Agence"
            price="169"
            desc="Équipe jusqu'à 5 agents avec vitrines unifiées."
            features={[
              "40 fiches / mois",
              "Multi-utilisateurs (5)",
              "Branding multi-vitrines",
            ]}
            onClick={() => go("pricing")}
          />
        </div>
      </section>

      <footer className="border-t border-ink-200/60">
        <div className="max-w-[1240px] mx-auto px-4 sm:px-6 py-8 sm:py-14 grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-10">
          <div className="col-span-2 md:col-span-2">
            <Logo />
            <p className="mt-4 text-[13.5px] text-ink-600 max-w-[320px] leading-relaxed">
              Arpentia révèle le potentiel d’urbanisme d’un bien, en 60 secondes,
              pour les agents immobiliers. L'IA qui arpente comme un expert.
            </p>
            <div className="mt-6 flex items-center gap-2">
              <Pill tone="soft" icon={<Ic.Check size={11} />}>
                ISO 27001 · en cours
              </Pill>
              <Pill tone="soft" icon={<Ic.Shield size={11} />}>
                RGPD
              </Pill>
            </div>
          </div>
          <FooterCol
            title="Produit"
            items={["Fonctionnalités", "Tarifs", "Intégrations", "Changelog"]}
          />
          <FooterCol
            title="Ressources"
            items={["Lexique urbanisme", "Guide PLU", "Blog", "Webinaires"]}
          />
          <FooterCol
            title="Société"
            items={["Notre histoire", "Clients", "Presse", "Contact"]}
            onItemClick={(item) => {
              if (item === "Notre histoire") go("brand");
            }}
          />
        </div>
        <div className="border-t border-ink-200/60">
          <div className="max-w-[1240px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4 text-[12px] text-ink-500 flex-wrap py-3 sm:py-0">
            <span>© 2026 Arpentia SAS — 8 rue d’Uzès, 75002 Paris.</span>
            <div className="flex items-center gap-4 sm:gap-5 flex-wrap">
              <button
                onClick={() => {
                  window.location.hash = "#mentions-legales";
                  go("legal");
                }}
                className="hover:text-ink-900 transition"
              >
                Mentions légales
              </button>
              <button
                onClick={() => {
                  window.location.hash = "#cgu";
                  go("legal");
                }}
                className="hover:text-ink-900 transition"
              >
                CGU
              </button>
              <button
                onClick={() => {
                  window.location.hash = "#confidentialite";
                  go("legal");
                }}
                className="hover:text-ink-900 transition"
              >
                Confidentialité
              </button>
              <button
                onClick={openCookieSettings}
                className="hover:text-ink-900 transition"
              >
                Gérer mes cookies
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ---------------- Helpers ---------------- */

const HeroVisual = () => (
  <div className="relative">
    <div className="absolute -inset-10 -z-0 rounded-[32px] bg-lime-200 opacity-50 blur-2xl" />
    <Card className="relative p-4 shadow-pop">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Pill tone="lime" icon={<Ic.Dot size={8} />}>
            Fiche potentiel
          </Pill>
          <span className="text-[11px] text-ink-500 font-mono">F-24018</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 rounded-full bg-ink-200" />
          <div className="w-2.5 h-2.5 rounded-full bg-ink-200" />
          <div className="w-2.5 h-2.5 rounded-full bg-ink-200" />
        </div>
      </div>
      <div className="px-2 pt-2 pb-3">
        <div className="text-[11px] text-ink-500">
          12 rue des Lilas · Boulogne-Billancourt
        </div>
        <div className="mt-1 flex items-baseline gap-3">
          <div className="text-[44px] leading-none font-semibold tracking-tight text-ink-900 tabular">
            +47
          </div>
          <div className="text-[12px] text-ink-600 pb-1">
            m² d’extension autorisés
          </div>
        </div>
        <div className="mt-1 text-[12px] text-forest font-medium">
          ≈ +280 k€ de valeur potentielle
        </div>
      </div>
      <Cadastre className="h-[180px]" />
      <div className="grid grid-cols-2 gap-2 mt-3">
        <MiniKpi label="Zone PLU" value="UA" />
        <MiniKpi label="Emprise max" value="60%" />
        <MiniKpi label="Hauteur max" value="12 m" />
        <MiniKpi label="CES résiduel" value="32 m²" />
      </div>
    </Card>

    <div className="absolute -left-6 top-20 hidden md:block">
      <div className="bg-ink-900 text-white rounded-full pl-1 pr-4 py-1 flex items-center gap-2 shadow-pop">
        <span className="w-7 h-7 rounded-full bg-lime-400 grid place-items-center text-ink-900">
          <Ic.Bolt size={14} />
        </span>
        <span className="text-[12px]">Généré en 54 s</span>
      </div>
    </div>
    <div className="absolute -right-4 bottom-20 hidden md:block">
      <Card className="px-3 py-2 flex items-center gap-2">
        <Ic.TrendUp size={14} className="text-forest" />
        <span className="text-[11.5px]">
          3 ventes comparables · 9 420 €/m²
        </span>
      </Card>
    </div>
  </div>
);

const MiniKpi = ({ label, value }: { label: string; value: string }) => (
  <div className="bg-ink-50 rounded-lg px-3 py-2">
    <div className="text-[10.5px] text-ink-500">{label}</div>
    <div className="text-[14px] font-semibold tabular">{value}</div>
  </div>
);

const TrustStrip = () => {
  const brands = [
    { name: "Hexagone", glyph: "◆" },
    { name: "Maisonnée", glyph: "▲" },
    { name: "Parcelle", glyph: "■" },
    { name: "Arpentage", glyph: "◉" },
    { name: "Cadastre&Co", glyph: "✦" },
    { name: "Toiture Neuve", glyph: "◇" },
  ];
  return (
    <div className="border-y border-ink-200/60 bg-paper/60">
      <div className="max-w-[1240px] mx-auto px-4 sm:px-6 py-5 sm:py-6 flex items-center gap-4 sm:gap-8 flex-wrap justify-between">
        <span className="text-[11.5px] tracking-[0.14em] uppercase text-ink-500">
          Utilisé par +420 agences
        </span>
        <div className="flex items-center gap-5 sm:gap-10 flex-wrap opacity-70">
          {brands.map((b) => (
            <div
              key={b.name}
              className="flex items-center gap-2 text-ink-700"
            >
              <span className="text-[18px]">{b.glyph}</span>
              <span className="text-[14px] font-medium tracking-tight">
                {b.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const StepCard = ({
  n,
  title,
  desc,
  icon,
  tone,
}: {
  n: string;
  title: string;
  desc: string;
  icon: ReactNode;
  tone?: "lime";
}) => (
  <Card className={`p-6 ${tone === "lime" ? "bg-lime-200 !shadow-none" : ""}`}>
    <div className="flex items-center justify-between">
      <div className="w-10 h-10 rounded-full bg-ink-900 text-white grid place-items-center">
        {icon}
      </div>
      <span className="font-mono text-[11px] text-ink-500">{n}</span>
    </div>
    <div className="mt-6 text-[19px] font-semibold tracking-tight">{title}</div>
    <p className="mt-2 text-[13.5px] text-ink-600 leading-relaxed">{desc}</p>
  </Card>
);

const MomentCard = ({
  tag,
  title,
  quote,
  body,
  features,
  visual,
  tone,
}: {
  tag: string;
  title: string;
  quote: string;
  body: string;
  features: string[];
  visual: "mandat" | "acheteur";
  tone?: "lime";
}) => (
  <Card
    className={`p-7 flex flex-col ${
      tone === "lime" ? "bg-lime-200 !shadow-none" : ""
    }`}
  >
    <Pill tone={tone === "lime" ? "soft" : "lime"}>{tag}</Pill>
    <h3 className="mt-4 text-[28px] leading-tight font-semibold tracking-tight">
      {title}
    </h3>
    <blockquote className="mt-4 pl-4 border-l-2 border-ink-900/30 text-[15px] italic font-serif text-ink-800 leading-snug">
      “{quote}”
    </blockquote>
    <p className="mt-4 text-[14px] text-ink-700 leading-relaxed">{body}</p>
    <ul className="mt-5 grid gap-2">
      {features.map((f) => (
        <li key={f} className="flex items-center gap-2 text-[13px]">
          <span className="w-5 h-5 rounded-full bg-paper hairline grid place-items-center">
            <Ic.Check size={12} />
          </span>
          {f}
        </li>
      ))}
    </ul>
    <div className="mt-6 rounded-lg overflow-hidden hairline">
      {visual === "mandat" ? <MandatIllu /> : <AcheteurIllu />}
    </div>
  </Card>
);

const MandatIllu = () => (
  <div className="h-[160px] relative map-grid">
    <div className="absolute inset-4 bg-paper hairline rounded-lg p-3 flex items-center gap-3">
      <div className="w-14 h-14 rounded-md stripe-placeholder hairline" />
      <div className="flex-1">
        <div className="text-[11px] text-ink-500">Estimation initiale</div>
        <div className="text-[18px] font-semibold">820 000 €</div>
        <div className="mt-1 flex items-center gap-2">
          <Ic.TrendUp size={12} className="text-forest" />
          <span className="text-[11px] text-forest">
            +280 000 € de potentiel PLU
          </span>
        </div>
      </div>
    </div>
  </div>
);

const AcheteurIllu = () => (
  <div className="h-[160px] relative bg-ink-900 p-4 flex items-center">
    <div className="flex-1">
      <div className="text-[11px] text-lime-300 font-mono uppercase tracking-widest">
        Email envoyé
      </div>
      <div className="text-white text-[14px] mt-2 font-medium">
        Potentiel du 12 rue des Lilas
      </div>
      <div className="text-ink-400 text-[11.5px] mt-1">
        Pièce jointe · fiche-plu-F24018.pdf · 2,1 Mo
      </div>
      <div className="mt-3 flex items-center gap-2">
        <span className="w-6 h-6 rounded-full bg-lime-400 grid place-items-center text-ink-900">
          <Ic.Check size={12} />
        </span>
        <span className="text-[11.5px] text-ink-300">
          Ouvert il y a 3 min · 2 fois
        </span>
      </div>
    </div>
    <div className="w-16 h-20 stripe-placeholder rounded-md" />
  </div>
);

const FeatureCard = ({
  icon,
  title,
  desc,
}: {
  icon: ReactNode;
  title: string;
  desc: string;
}) => (
  <Card className="p-6 group hover:shadow-pop transition">
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-lime-200 hairline grid place-items-center">
        {icon}
      </div>
      <div className="text-[15.5px] font-semibold tracking-tight">{title}</div>
    </div>
    <p className="mt-3 text-[13.5px] text-ink-600 leading-relaxed">{desc}</p>
  </Card>
);

const Testimonial = ({
  name,
  role,
  body,
  tone,
}: {
  name: string;
  role: string;
  body: string;
  tone?: "lime";
}) => (
  <Card
    className={`p-6 ${
      tone === "lime"
        ? "bg-lime-300 !shadow-none hairline text-ink-900"
        : "bg-ink-800 text-white border-0 !shadow-none"
    }`}
  >
    <Ic.Quote
      size={22}
      className={tone === "lime" ? "text-ink-900" : "text-lime-300"}
    />
    <p
      className={`mt-4 text-[16px] leading-relaxed font-serif italic ${
        tone === "lime" ? "text-ink-900" : "text-white"
      }`}
    >
      « {body} »
    </p>
    <div className="mt-6 flex items-center gap-3">
      <div
        className={`w-9 h-9 rounded-full grid place-items-center text-[12px] font-semibold
          ${
            tone === "lime"
              ? "bg-ink-900 text-lime-300"
              : "bg-lime-400 text-ink-900"
          }`}
      >
        {name
          .split(" ")
          .map((s) => s[0])
          .join("")}
      </div>
      <div>
        <div
          className={`text-[13.5px] font-medium ${
            tone === "lime" ? "text-ink-900" : "text-white"
          }`}
        >
          {name}
        </div>
        <div
          className={`text-[11.5px] ${
            tone === "lime" ? "text-ink-700" : "text-ink-400"
          }`}
        >
          {role}
        </div>
      </div>
    </div>
  </Card>
);

const PricingTeaser = ({
  plan,
  price,
  desc,
  features,
  highlight,
  onClick,
}: {
  plan: string;
  price: string;
  desc: string;
  features: string[];
  highlight?: boolean;
  onClick?: () => void;
}) => (
  <Card className={`p-7 ${highlight ? "ring-2 ring-ink-900" : ""}`}>
    <div className="flex items-center justify-between">
      <div className="text-[13px] font-medium text-ink-600">{plan}</div>
      {highlight && <Pill tone="lime">Populaire</Pill>}
    </div>
    <div className="mt-3 flex items-baseline gap-1.5">
      <span className="text-[44px] font-semibold tracking-tight tabular">
        {price}€
      </span>
      <span className="text-[13px] text-ink-500">/ mois</span>
    </div>
    <p className="text-[13px] text-ink-600 mt-2">{desc}</p>
    <ul className="mt-5 space-y-2">
      {features.map((f) => (
        <li key={f} className="flex items-center gap-2 text-[13px]">
          <Ic.Check size={14} className="text-forest" /> {f}
        </li>
      ))}
    </ul>
    <Button
      className="mt-6 w-full"
      variant={highlight ? "dark" : "soft"}
      onClick={onClick}
    >
      Choisir {plan}
    </Button>
  </Card>
);

const FooterCol = ({
  title,
  items,
  onItemClick,
}: {
  title: string;
  items: string[];
  onItemClick?: (item: string) => void;
}) => (
  <div>
    <div className="text-[12px] uppercase tracking-[0.14em] text-ink-500 mb-4">
      {title}
    </div>
    <ul className="space-y-2.5">
      {items.map((i) => (
        <li
          key={i}
          className="text-[13.5px] text-ink-700 hover:text-ink-900"
        >
          <a
            href="#"
            onClick={(e) => {
              if (onItemClick) {
                e.preventDefault();
                onItemClick(i);
              }
            }}
          >
            {i}
          </a>
        </li>
      ))}
    </ul>
  </div>
);
